import type { CSSProperties } from 'react'

// ONE series-style vocabulary shared by every chart renderer in the suite -
// ECharts (via aria decals) and the CSS/text bars (via background images) - so a
// series looks identical whichever draws it. The suite is blue-forward and a few
// shades of one blue are genuinely hard to tell apart, so each slot pairs a colour
// with a distinct TEXTURE: texture, not just lightness, carries identity. Order
// is fixed (never cycled per-render) so a category keeps its look across charts.
//
// Colour identifies the DOMAIN (gases = blue, green-power = amber); reductions render
// in the brand blue on dashboard summaries, while their detailed chart keeps green
// (coloured in-app). Lightness + texture identify the MEMBER within a domain. See
// docs/superpowers/specs/2026-07-17-tellus-color-system-design.md.

export type TextureKind = 'solid' | 'fwd' | 'back' | 'dots'

export interface SeriesStyle {
  color: string
  texture: TextureKind
}

// Generic blue ramp - the default for non-domain multi-series charts. Adjacent
// slots differ in BOTH lightness and texture.
export const SERIES_STYLES: SeriesStyle[] = [
  { color: '#0067a5', texture: 'solid' }, // perfect-shine, solid
  { color: '#0084d6', texture: 'fwd' },   // bright blue, forward hatch
  { color: '#74a5c3', texture: 'back' },  // light blue, back hatch
  { color: '#00436c', texture: 'dots' },  // navy, dots
  { color: '#99bdd3', texture: 'fwd' },   // tint, forward hatch
  { color: '#a5bcca', texture: 'back' },  // pale, back hatch
]

// --- Domain palettes: hue = domain, lightness = member. ----------------------
// Members are told apart by SHADE alone (three well-spaced steps). Texture is NOT
// used to distinguish members: it is reserved for a single meaning across the
// suite - pending / in-progress status (the crosshatch overlay in the manual
// reductions and projections charts) - so a hatch never means two different
// things. (The generic SERIES_STYLES ramp above still uses texture, for charts
// with more series than shades can separate.)

// Greenhouse gases: three DISTINCT hues (CO2 blue, N2O green, CH4 amber), matching
// the Footprint emissions chart. Gases are a categorical set, so distinct hues read
// apart far better than three shades of one blue; solid, no texture.
export const GAS_STYLES: SeriesStyle[] = [
  { color: '#0067a5', texture: 'solid' }, // CO2 (Tellus blue)
  { color: '#2e7a54', texture: 'solid' }, // N2O (green)
  { color: '#DEAC50', texture: 'solid' }, // CH4 (amber)
]

// Reductions - the primary Tellus blue on the dashboard summary cards, so the carbon
// dashboard matches the ppa dashboard (which is blue because it groups by location).
// The stat/bars cards use only the hero (#0067a5); the ramp gives distinct steps for
// any composition/legend. NOTE: the detailed manual-reductions chart is coloured
// independently in sustain's Action.tsx and stays green - this palette drives the
// CategoryBreakdownChart dashboards, not that chart.
export const REDUCTION_STYLES: SeriesStyle[] = [
  { color: '#0067a5', texture: 'solid' }, // REC (primary Tellus blue - dashboard hero)
  { color: '#0084d6', texture: 'solid' }, // SREC (bright blue)
  { color: '#74a5c3', texture: 'solid' }, // VER (light blue)
]

// Green-power types - amber, the warm counterpoint; solar anchors it.
export const GREENPOWER_STYLES: SeriesStyle[] = [
  { color: '#d69a3c', texture: 'solid' }, // solar (hero)
  { color: '#a86a1f', texture: 'solid' }, // wind
  { color: '#ecc57e', texture: 'solid' }, // geothermal
]

export const SERIES_COLORS = SERIES_STYLES.map((s) => s.color)
export const GAS_COLORS = GAS_STYLES.map((s) => s.color)
export const REDUCTION_COLORS = REDUCTION_STYLES.map((s) => s.color)
export const GREENPOWER_COLORS = GREENPOWER_STYLES.map((s) => s.color)

// --- CSS side: base color + a white texture overlay, as a pure fn of a style. -
const stripe = (deg: number) =>
  `repeating-linear-gradient(${deg}deg, rgba(255,255,255,0) 0 4px, rgba(255,255,255,0.34) 4px 6px)`
const DOTS = 'radial-gradient(rgba(255,255,255,0.45) 1.1px, transparent 1.3px)'

export const styleBackground = (s: SeriesStyle): CSSProperties => {
  switch (s.texture) {
    case 'fwd':
      return { backgroundColor: s.color, backgroundImage: stripe(-45) }
    case 'back':
      return { backgroundColor: s.color, backgroundImage: stripe(45) }
    case 'dots':
      return { backgroundColor: s.color, backgroundImage: DOTS, backgroundSize: '6px 6px' }
    default:
      return { backgroundColor: s.color }
  }
}

// --- ECharts side: the matching decal for a style (kept in lockstep). --------
export const styleDecal = (s: SeriesStyle) => {
  switch (s.texture) {
    case 'fwd':
      return { color: 'rgba(255,255,255,0.42)', dashArrayX: [1, 0], dashArrayY: [3, 5], rotation: -Math.PI / 4 }
    case 'back':
      return { color: 'rgba(255,255,255,0.42)', dashArrayX: [1, 0], dashArrayY: [3, 5], rotation: Math.PI / 4 }
    case 'dots':
      return { color: 'rgba(255,255,255,0.5)', symbol: 'circle', symbolSize: 0.4, dashArrayX: [[4, 4]], dashArrayY: [4, 4] }
    default:
      return { dashArrayX: 0, dashArrayY: 0, color: 'rgba(0,0,0,0)' }
  }
}

// Index-based helpers over the generic ramp (back-compat with existing callers).
export const seriesBackground = (index: number): CSSProperties =>
  styleBackground(SERIES_STYLES[index % SERIES_STYLES.length])
export const seriesColor = (index: number): string => SERIES_STYLES[index % SERIES_STYLES.length].color

export const ECHARTS_DECALS = SERIES_STYLES.map(styleDecal)
export const GAS_DECALS = GAS_STYLES.map(styleDecal)
export const REDUCTION_DECALS = REDUCTION_STYLES.map(styleDecal)
export const GREENPOWER_DECALS = GREENPOWER_STYLES.map(styleDecal)

// The ONE pending / in-progress crosshatch: thin white diagonal lines laid over a
// solid segment colour (manual-reductions goal bar and its legend swatch). Kept as a
// single shared constant so the bar and its legend can never drift apart. 2px stripes
// on a 6px pitch at 0.5 opacity read clearly on the domain shades without washing the
// fill out (the earlier 0.32 / 11px version was too faint on the goal bar).
export const PENDING_HATCH =
  'repeating-linear-gradient(45deg, rgba(255,255,255,0.5) 0 2px, transparent 2px 6px)'

// --- Semantic overlay colours (target charts: projections, manual reductions).
// The bar stays the domain colour; meaning is layered on top.
export const SEMANTIC = {
  shortfall: '#D83527',  // the one reserved red - only the amount behind target
  remaining: '#93a6b4',  // neutral gray - unfilled / absence (replaces old yellow)
  targetLine: '#002338', // navy (TELLUS_EMPHASIS) - the target line
} as const
